<script setup lang="ts">
import { computed } from 'vue'
import type { PlayerUpgrade } from '@/types/shared/player-config'
import { T, useTranslate } from '@tolgee/vue'
import CheckCircleGreenBorder from '@/components/icons/CheckCircleGreenBorder.vue'
import { useWatchOverlays } from '@/composables/helpers/scoll'

const props = defineProps<{
  comboCards: PlayerUpgrade[]
  show: boolean
}>()

const comboNumber = computed(() => props.comboCards.length)
const { t } = useTranslate()

const text = computed(() => {
  if (comboNumber.value === 1) return t.value('daily_combo.first_card_discovered', `First card of the combo collected!\nKeep going!`)
  if (comboNumber.value === 2) return t.value('daily_combo.second_card_discovered', `You’ve found the second card, just one more to go!`)
  return ''
})

const currentCard = computed(() => props.comboCards[props.comboCards.length - 1])

useWatchOverlays(computed(() => props.show))

</script>

<template>
  <Transition
    name="fade"
    mode="out-in"
  >
    <div
      v-if="show"
      class="fixed h-screen max-h-dvh w-screen overflow-hidden left-0 bottom-0 transition-all duration-300 bg-dark-most-bottom-layer/[.95] flex flex-col z-[800]"
    >
      <div class="h-full w-full flex-1 flex flex-col">
        <div class="-mt-[68px] p-8 h-full w-full flex flex-col items-center justify-center">
          <div class="mt-[80px] mb-8 text-[36px] leading-[42px] font-semibold z-[1]">
            <T
              key-name="daily_combo.title"
              default-value="Project assets"
            />
          </div>
          <div class="mb-8 w-[212px] min-w-[212px] h-[212px] relative">
            <div
              class="w-[120%] h-[120%] absolute -top-[10%] -left-[10%] rounded-full bg-green-primary/[.54] blur-[43px]"
            />
            <div
              class="w-full h-full bg-black/[0.12] rounded-xl backdrop-blur-[16px] flex flex-col items-center justify-center gap-y-2 border-2 border-[#E8FFBA] px-3"
            >
              <img
                v-if="currentCard"
                :src="currentCard.imageUrl"
                class="h-[92px] max-h-[92px] w-auto object-cover"
                alt="card"
              >
              <span
                v-if="currentCard"
                class="text-center text-[22px] font-semibold line-clamp-2 text-white"
              >
                {{ currentCard.name }}
              </span>
            </div>
          </div>
          <div class="text-lg leading-6 text-center font-semibold max-w-[315px] z-[1] whitespace-pre-line">
            {{ text }}
          </div>
          <div class="absolute bottom-12 flex items-center gap-x-4">
            <div
              v-for="n in 3"
              :key="n"
              class="w-6 h-6 min-w-[24px] rounded-full flex items-center justify-center bg-transparent"
              :class="comboNumber < n ? 'border-2 border-white/20' : ''"
            >
              <CheckCircleGreenBorder
                v-if="comboNumber >= n"
                wrapper-class="w-full h-full min-w-[24px]"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
</style>
